import { TemplateTabs } from './TemplateCandidateReview'
import { useState, useEffect, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useListSearchParams } from '@/hooks/useListSearchParams'
import { useContentReadOnly } from '@/hooks/useConfig'
import { useTemplates, useDeleteTemplate, useCategories } from '../api/admin-api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Pencil, Trash2, ImageIcon } from 'lucide-react'
import { ImageWithFallback } from '@/components/common/ImageWithFallback'
import { toast } from 'sonner'

type SortOption = {
  sortBy: 'created_at' | 'updated_at' | 'list_order' | 'name' | 'usage_count'
  sortOrder: 'asc' | 'desc'
  label: string
}

const SORT_OPTIONS: SortOption[] = [
  { sortBy: 'created_at', sortOrder: 'desc', label: '최신순' },
  { sortBy: 'created_at', sortOrder: 'asc', label: '오래된순' },
  { sortBy: 'usage_count', sortOrder: 'desc', label: '인기순' },
  { sortBy: 'list_order', sortOrder: 'asc', label: '정렬순서 (오름차순)' },
  { sortBy: 'list_order', sortOrder: 'desc', label: '정렬순서 (내림차순)' },
  { sortBy: 'name', sortOrder: 'asc', label: '이름 (ㄱ-ㅎ)' },
  { sortBy: 'name', sortOrder: 'desc', label: '이름 (ㅎ-ㄱ)' },
  { sortBy: 'updated_at', sortOrder: 'desc', label: '수정일 (최신순)' },
]

export function TemplateList() {
  const navigate = useNavigate()
  const { page, filters, setPage, setFilter } = useListSearchParams({
    search: '',
    enabled: 'all',
    categoryId: '',
    sort: 0,
  })
  const [searchInput, setSearchInput] = useState(filters.search)

  // Debounce search input → sync to URL
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== filters.search) {
        setFilter('search', searchInput)
      }
    }, 500)

    return () => clearTimeout(timer)
  }, [searchInput, filters.search, setFilter])

  const { data: categories } = useCategories()
  const categoriesMap = useMemo(
    () => new Map(categories?.map((c) => [c.id, c]) ?? []),
    [categories],
  )
  const currentSort = SORT_OPTIONS[filters.sort] ?? SORT_OPTIONS[0]

  const { data, isLoading, error } = useTemplates({
    page,
    pageSize: 20,
    search: filters.search || undefined,
    enabled: filters.enabled as 'true' | 'false' | 'all',
    categoryId: filters.categoryId || undefined,
    sortBy: currentSort.sortBy,
    sortOrder: currentSort.sortOrder,
  })

  const deleteMutation = useDeleteTemplate()
  const readOnly = useContentReadOnly()

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`"${name}" 템플릿을 삭제하시겠습니까?`)) return

    try {
      await deleteMutation.mutateAsync(id)
      toast.success('템플릿이 삭제되었습니다')
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : '삭제 중 오류가 발생했습니다')
    }
  }

  if (isLoading) {
    return <div className="text-center py-12 text-gray-500">로딩 중...</div>
  }

  if (error) {
    return <div className="text-center py-12 text-red-500">에러: {error.message}</div>
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-heading tracking-tight">템플릿</h1>
          <p className="text-caption mt-2 leading-relaxed">프롬프트 템플릿 관리</p>
        </div>
        {!readOnly && (
          <Link to="/admin/templates/new">
            <Button>+ 새 템플릿</Button>
          </Link>
        )}
      </div>

      <TemplateTabs />

      {/* Filters */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Input
            placeholder="검색..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="max-w-xs"
          />
          <select
            value={filters.categoryId}
            onChange={(e) => setFilter('categoryId', e.target.value)}
            className="pl-3 pr-10 py-2 border border-gray-300 rounded-lg text-sm bg-white appearance-none cursor-pointer"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E")`,
              backgroundPosition: 'right 0.5rem center',
              backgroundRepeat: 'no-repeat',
              backgroundSize: '1.5em 1.5em',
            }}
          >
            <option value="">모든 카테고리</option>
            {categories?.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
          </select>
          <select
            value={filters.enabled}
            onChange={(e) => setFilter('enabled', e.target.value)}
            className="pl-3 pr-10 py-2 border border-gray-300 rounded-lg text-sm bg-white appearance-none cursor-pointer"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E")`,
              backgroundPosition: 'right 0.5rem center',
              backgroundRepeat: 'no-repeat',
              backgroundSize: '1.5em 1.5em',
            }}
          >
            <option value="all">전체 상태</option>
            <option value="true">활성</option>
            <option value="false">비활성</option>
          </select>
        </div>
        <select
          value={filters.sort}
          onChange={(e) => setFilter('sort', Number(e.target.value))}
          className="pl-3 pr-10 py-2 border border-gray-300 rounded-lg text-sm bg-white appearance-none cursor-pointer"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E")`,
            backgroundPosition: 'right 0.5rem center',
            backgroundRepeat: 'no-repeat',
            backgroundSize: '1.5em 1.5em',
          }}
        >
          {SORT_OPTIONS.map((opt, idx) => (
            <option key={idx} value={idx}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 min-w-[800px]">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                썸네일
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase w-72">
                이름
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                카테고리
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                비율
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                프레임
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                순서
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                사용
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                액션
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {data?.items.map((template) => (
              <tr
                key={template.id}
                className="hover:bg-gray-50 cursor-pointer"
                onClick={() => navigate(`/admin/templates/${template.id}/view`)}
              >
                <td className="px-6 py-4">
                  {template.thumbnailUrl ? (
                    <ImageWithFallback
                      src={`${template.thumbnailUrl}?v=${new Date(template.updatedAt).getTime()}`}
                      alt={template.name}
                      className="w-10 h-10 object-cover rounded border"
                    />
                  ) : (
                    <div className="w-10 h-10 bg-gray-100 rounded border flex items-center justify-center">
                      <ImageIcon className="w-5 h-5 text-gray-400" />
                    </div>
                  )}
                </td>
                <td className="px-6 py-4">
                  <div className="font-medium text-gray-900">
                    {template.name}
                  </div>
                  {template.description && (
                    <div className="text-sm text-gray-500 line-clamp-1">
                      {template.description}
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 text-sm text-gray-700">
                  {(template.categoryId && categoriesMap.get(template.categoryId)?.name) || '-'}
                </td>
                <td className="px-6 py-4 text-sm text-gray-700">
                  {template.ratio}
                </td>
                <td className="px-6 py-4 text-sm text-gray-700">
                  {template.frameImageCount || 0}개
                </td>
                <td className="px-6 py-4 text-sm text-gray-700">
                  {template.listOrder}
                </td>
                <td className="px-6 py-4 text-sm text-gray-700">
                  {template.usageCount || 0}회
                </td>
                <td className="px-6 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                  {!readOnly && (
                    <div className="flex items-center justify-end gap-2">
                      <Link to={`/admin/templates/${template.id}/edit`}>
                        <Button variant="ghost" size="sm">
                          <Pencil className="w-4 h-4" />
                        </Button>
                      </Link>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(template.id, template.name)}
                        disabled={deleteMutation.isPending}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {data?.items.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            템플릿이 없습니다
          </div>
        )}
      </div>

      {/* Pagination */}
      {data && (
        <div className="flex items-center justify-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page === 1}
          >
            이전
          </Button>
          <span className="text-sm text-gray-600">
            {page} / {data.pagination.totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(Math.min(data.pagination.totalPages, page + 1))}
            disabled={page === data.pagination.totalPages}
          >
            다음
          </Button>
        </div>
      )}
    </div>
  )
}
